import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ShopService } from '@services/shops/shop.service';
import { Shop } from '@services/shops/shop.model';
import { Observable, Subject } from 'rxjs';
import { ExtensionComponentType, ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { VipProcessConfig } from '@app/pages/checkout-app/checkout-layout/main/components/extensions/components/extension-components/vip-process/model';
import { VipProcessDto } from '@app/pages/checkout-app/checkout-layout/main/components/extensions/components/extension-components/vip-process/vip-process.dto';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { CheckoutReducer } from '@pages/checkout-app/checkout-store/reducers';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { takeUntil } from 'rxjs/operators';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import { ViewMode } from '@pages/checkout-app/checkout-layout/main/components/extensions/models';

@Component({
  selector: 'vip-process',
  templateUrl: './vip-process.component.html',
  styleUrls: ['./vip-process.component.scss'],
  standalone: false,
})
export class VipProcessComponent implements OnInit, OnDestroy {
  @onDestroyComplete() destroy$ = new Subject();
  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  vipProcessConfig = [...VipProcessConfig];

  formGroup: UntypedFormGroup;
  shop$: Observable<Shop>;
  constructor(
    private shopService: ShopService,
    private formBuilder: UntypedFormBuilder,
    private store: Store<fromCheckoutReducer.State>,
    private extensionsService: ExtensionsService,
    private router: Router,
  ) {}
  ngOnDestroy() {}

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.VIP_PROCESS);
    this.extensionsService.save$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.saveValues(this.formGroup.value);
    });

    this.shop$ = this.shopService.shop$;
    this.formGroup = this.formBuilder.group(this.initForm());

    this.loadedExtension$.pipe(takeUntil(this.destroy$)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.formGroup = this.formBuilder.group(this.initForm(extension.template));
      }
    });

    this.initChangesForExampleView();
    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });
    this.extensionsService.setFormValid(this.formGroup.valid);
  }

  initChangesForExampleView() {
    const exampleTemplate = new VipProcessDto().createExampleTemplate();
    this.dispatchExampleViewValues(this.loadedExtension?.template ?? exampleTemplate);

    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });
  }

  dispatchExampleViewValues(values) {
    if (!values) {
      return;
    }

    const template = {
      title: values.title,
      description: values.description,
      upsellRate: values.upsellRate,
      discount: values.discount,
    };

    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.CUSTOM_UPSELL]: {
        mode,
        template,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  initForm(loadedValues?: any) {
    const newValues = {};
    this.vipProcessConfig.forEach((field) => {
      newValues[field.fieldName] = [
        loadedValues ? loadedValues[field.fieldName] : field.defaultValue,
        field.validators,
      ];
    });
    return newValues;
  }

  saveValues(values) {
    const template = { ...values };
    const vipProcessDTO = new VipProcessDto();

    const vipProcessObject: Partial<ICheckoutExtensions> = {
      template: vipProcessDTO.createVIProcessTemplateDTO(template),
      type: ExtensionComponentType.VIP_PROCESS,
    };

    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: { ...vipProcessObject, id } }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: vipProcessObject }));
    }
  }
  cancel() {
    this.store.dispatch(CheckoutActions.resetActiveExtension());
    this.store.dispatch(CheckoutActions.resetLiveExampleTemplateView());
    this.extensionsService.resetExtensionState();
    this.router.navigate(['/convert/extensions']);
  }
}
